import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  type WriteBatch,
} from "firebase/firestore";
import { db } from "../firebase";

/** Append-only log of a bus leaving or returning to service.
 *
 *  `buses.status` is only the CURRENT state — it is overwritten on activate,
 *  so a suspension that later ends leaves no trace. This collection is the
 *  record that survives that flip. Old suspensions from before this shipped
 *  have no rows; that is the same class of hole as parkedRoster (bug 74). */
export const BUS_STATUS_EVENTS = "busStatusEvents";

export type BusServiceStatus = "active" | "maintenance" | "suspended" | "for_hire";

export type BusStatusEventInput = {
  busId: string;
  orgCode: string;
  plate: string;
  busName: string;
  fromStatus: string;
  toStatus: string;
  byUid?: string | null;
  byEmail?: string | null;
  source: "dashboard" | "zana" | "hire_expiry";
  childCount?: number;
};

export function busStatusEventPayload(input: BusStatusEventInput) {
  return {
    busId: input.busId,
    orgCode: input.orgCode,
    schoolCode: input.orgCode,
    plate: input.plate,
    busName: input.busName,
    fromStatus: input.fromStatus,
    toStatus: input.toStatus,
    at: serverTimestamp(),
    byUid: input.byUid ?? null,
    byEmail: input.byEmail ?? null,
    source: input.source,
    childCount: input.childCount ?? 0,
  };
}

function shouldLog(input: BusStatusEventInput): boolean {
  return Boolean(input.orgCode && input.busId && input.fromStatus !== input.toStatus);
}

/** Fire-and-forget write. A history failure must not block suspend/activate. */
export async function logBusStatusEvent(input: BusStatusEventInput): Promise<void> {
  if (!shouldLog(input)) return;
  try {
    await addDoc(collection(db, BUS_STATUS_EVENTS), busStatusEventPayload(input));
  } catch (e) {
    console.error("busStatusEvents write failed", e);
  }
}

export function appendBusStatusEvent(batch: WriteBatch, input: BusStatusEventInput): void {
  if (!shouldLog(input)) return;
  batch.set(doc(collection(db, BUS_STATUS_EVENTS)), busStatusEventPayload(input));
}
